import { HomeRounded, HomeWorkRounded } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Grid } from "@mui/material";
import theme from "Theme/AppTheme";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import { observer } from "mobx-react";
import { Options } from "react-to-pdf";
import { useStore } from "utils/useStore";
import SignalsReportSelectorStore from "./AddCards/SignalsCardSelectorStore";
import { FavoriteToggle } from "components/UI/FavoriteToggle";
import { useCurrentDashboard } from "../useCurrentDashboard";
import { SignalsDashboardType } from "../SignalsModels";
import SignalsPageStore from "../Store/SignalsPageStore";
import { DashboardTitle } from "./DashboardTitle";
import { DashboardDescription } from "./DashboardDescription";
import { DashboardMenu } from "./DashboardMenu/DashboardMenu";
import { DashboardLogo } from "./DashboardLogo";
import { DashboardFiltersSection } from "./DashboardFiltersSection/DashboardFiltersSection";
import DashboardLayoutToolbar from "./Layout/DashboardLayoutToolbar";
import { AddCardsDialog } from "./AddCards/AddCardsDialog";

const DashboardHeader = observer(
    ({ toPDF }: { toPDF: (options?: Options) => Promise<void> }) => {
        const selectorStore = useStore(SignalsReportSelectorStore);

        const currentDashboard = useCurrentDashboard();
        const signalsPageStore = useStore(SignalsPageStore);

        const isCustomDashboard =
            currentDashboard?.dashboardType === SignalsDashboardType.Custom;

        const AddCard = () => {
            return currentDashboard?.dashboardIsEditable ? (
                <Grid item>
                    <AcxButton
                        id="signals-dashboard-add-report"
                        fullWidth
                        // Cards are added or deleted only outside arranging.
                        disabled={currentDashboard.isArranging}
                        onClick={() =>
                            (selectorStore.showReportSelector = true)
                        }
                        size="small"
                        startIcon={<AddIcon />}
                        sx={{
                            color: theme.palette.blackFont.main,
                            backgroundColor: theme.palette.white.main,
                            width: "115px",
                            border: "1px solid",
                            borderColor: theme.palette.gray.light,
                            height: "32px",
                            "&:hover": {
                                backgroundColor: theme.palette.gray.light,
                            },
                        }}
                    >
                        Add Card
                    </AcxButton>
                </Grid>
            ) : (
                <Grid
                    item
                    style={{
                        color: theme.palette.gray.main,
                        fontWeight: "600",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <VisibilityIcon sx={{ marginRight: theme.spacing(1) }} />
                    View Only
                </Grid>
            );
        };

        return (
            <Grid
                container
                sx={{
                    position: "sticky",
                    top: 0,
                    padding: theme.spacing(2),
                    width: "100%",
                    borderRadius: 0,
                    zIndex: 1098,
                    backgroundColor: theme.palette.lightgray.background,
                }}
            >
                <Grid
                    container
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    style={{ marginBottom: "16px" }}
                >
                    <Grid container item xs={8} wrap="nowrap">
                        <DashboardLogo />
                        {/* Title row and description stack in a column so the description
                            aligns under the title rather than under the logo. */}
                        <Grid
                            container
                            item
                            direction="column"
                            justifyContent={"center"}
                            sx={{ flex: 1, minWidth: 0 }}
                        >
                            <Grid container item alignItems={"center"}>
                                <DashboardTitle />
                                {currentDashboard?.isUserHomeDashboard && (
                                    <Grid
                                        item
                                        display={"flex"}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                        marginLeft={theme.spacing(2)}
                                    >
                                        <AcxChip
                                            style={{
                                                padding: "6px 4px 8px 4px",
                                                marginBottom: 0,
                                            }}
                                            size="small"
                                            color="blue"
                                            key={"home-chip"}
                                            label={"Home"}
                                            customMaterialIcon={<HomeRounded />}
                                        />
                                    </Grid>
                                )}
                                {currentDashboard?.isOrgHomeDashboard &&
                                    !currentDashboard?.isUserHomeDashboard && (
                                        <Grid
                                            item
                                            display={"flex"}
                                            alignItems={"center"}
                                            justifyContent={"center"}
                                            marginLeft={theme.spacing(2)}
                                            className="no-export"
                                        >
                                            <AcxChip
                                                style={{
                                                    padding: "6px 4px 8px 4px",
                                                    marginBottom: 0,
                                                }}
                                                size="small"
                                                color="blue"
                                                key={"home-chip"}
                                                label={"Org Home"}
                                                customMaterialIcon={
                                                    <HomeWorkRounded />
                                                }
                                            />
                                        </Grid>
                                    )}
                            </Grid>
                            <DashboardDescription />
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        item
                        xs={3}
                        justifyContent={"flex-end"}
                        gap={2}
                        alignItems={"center"}
                        className="no-export"
                    >
                        {!currentDashboard?.isPresentationMode &&
                            isCustomDashboard && <AddCard />}
                        {!currentDashboard?.isPresentationMode &&
                            !currentDashboard?.isStandardDashboard && (
                                <FavoriteToggle
                                    id="favorite-dashboard"
                                    favoriteFn={() => {
                                        if (currentDashboard?.id) {
                                            signalsPageStore.addFavorite(
                                                currentDashboard?.id,
                                            );
                                        }
                                    }}
                                    unfavoriteFn={() => {
                                        if (currentDashboard?.id) {
                                            signalsPageStore.removeFavorite(
                                                currentDashboard?.id,
                                            );
                                        }
                                    }}
                                    isFavorited={
                                        currentDashboard?.isFavorite || false
                                    }
                                />
                            )}
                        <DashboardMenu toPDF={toPDF} />
                    </Grid>
                </Grid>

                {currentDashboard?.isArranging ? (
                    <DashboardLayoutToolbar dashboardStore={currentDashboard} />
                ) : (
                    <DashboardFiltersSection />
                )}
                {currentDashboard?.isPresentationMode && (
                    <Grid
                        container
                        style={{
                            fontWeight: 700,
                            color: theme.palette.gray[500],
                        }}
                    >
                        Insights time frame:{" "}
                        {signalsPageStore.applicationFiltersStore.quickFiltersStore.startDate!.format(
                            "M/D/YYYY",
                        )}{" "}
                        -{" "}
                        {signalsPageStore.applicationFiltersStore.quickFiltersStore.endDate!.format(
                            "M/D/YYYY",
                        )}
                    </Grid>
                )}
                <AddCardsDialog />
            </Grid>
        );
    },
);
export default DashboardHeader;
